import { accommodationOfferPageScopeKey } from '@luxuryescapes/contract-trip'
import { onlyDefinedProperties, removeUndefinedAndNullProperties } from 'lib/object/objectUtils'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import type { DegradedSurfaceIdentity } from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import { nonEmptyString } from './offerPageContextFormatting'
import type { AccommodationOfferPageAssistantPageIdentityRequest } from './offerPageContextTypes'
import type { OfferPageAssistantSource } from './offerPageSnapshotTypes'
import { toOfferPageSnapshotLabel } from './offerPageSnapshotText'

export interface OfferPageSnapshotIdentity {
  offerId?: string
  propertyId?: string
  displayName?: string
}

export function resolveOfferPageSnapshotIdentity(source: OfferPageAssistantSource): OfferPageSnapshotIdentity | undefined {
  switch (source.variant) {
    case 'flash':
      return {
        offerId: nonEmptyString(source.offer.id),
        propertyId: nonEmptyString(source.offer.property?.id),
        displayName: nonEmptyString(source.offer.property?.name) ?? nonEmptyString(source.offer.name),
      }
    case 'lme_property':
    case 'spp_property':
      return {
        propertyId: nonEmptyString(source.property.id),
        displayName: nonEmptyString(source.property.name),
      }
    case 'bedbank':
      return {
        offerId: nonEmptyString(source.offer.id),
        propertyId: nonEmptyString(source.offer.property.id),
        displayName: nonEmptyString(source.offer.property.name) ?? nonEmptyString(source.offer.name),
      }
    default:
      return assertUnreachable(source)
  }
}

export function resolveOfferPageRegenerationIdentity(
  source: OfferPageAssistantSource,
): AccommodationOfferPageAssistantPageIdentityRequest | undefined {
  switch (source.variant) {
    case 'flash': {
      const offerId = nonEmptyString(source.offer.id)
      if (!offerId) {
        return undefined
      }

      const propertyId = nonEmptyString(source.offer.property?.id)

      return {
        offerId,
        ...(propertyId ? { propertyId } : {}),
      }
    }
    case 'bedbank': {
      const offerId = nonEmptyString(source.offer.id)

      return offerId ? { offerId } : undefined
    }
    case 'lme_property':
    case 'spp_property': {
      const propertyId = nonEmptyString(source.property.id)

      return propertyId ? { propertyId } : undefined
    }
    default:
      return assertUnreachable(source)
  }
}

export function buildOfferPageSnapshotScopeKey(
  identity: AccommodationOfferPageAssistantPageIdentityRequest,
): string | undefined {
  return accommodationOfferPageScopeKey(removeUndefinedAndNullProperties(identity))
}

export function buildOfferPageSnapshotScopeKeyFromSource(
  source: OfferPageAssistantSource,
): string | undefined {
  const identity = resolveOfferPageRegenerationIdentity(source)

  return identity ? buildOfferPageSnapshotScopeKey(identity) : undefined
}

export function buildOfferPageDegradedIdentity(
  source: OfferPageAssistantSource,
): DegradedSurfaceIdentity | undefined {
  const identity = resolveOfferPageSnapshotIdentity(source)
  const surfaceContextScopeKey = buildOfferPageSnapshotScopeKeyFromSource(source)
  if (!identity || !surfaceContextScopeKey) {
    return undefined
  }

  return onlyDefinedProperties({
    surface: 'accommodation_offer_page' as const,
    surfaceContextScopeKey,
    offerId: identity.offerId,
    propertyId: identity.propertyId,
    displayName: identity.displayName ? toOfferPageSnapshotLabel(identity.displayName) : undefined,
  })
}
